import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { format } from 'date-fns';
import {
  btnSize,
  btnType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Link,
  linkType,
  Text,
  textLevel,
} from '../../../../packages';
import { ProfileInfo } from '../../../../packages/ui/templates/profile-info';
import { useStyles } from '../ClaimDetail.styles';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

const defaultData = {
  dob: '',
  firstName: '',
  fullName: '',
  gender: '',
  lastName: '',
  phoneNumber: '',
  profileImage: '',
  ssn: '',
};

const Patient = ({ data = defaultData }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const SectionTitle = ({ title = '' }) => (
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 800,
        lineHeight: '20px',
        letterSpacing: '1px',
        width: '100%',
        textTransform: 'uppercase',
        color: colors.neutral900,
      }}
    >
      {title}
    </Typography>
  );

  const Col = ({ label, value, tel = false, email = false, link = false }) => (
    <Box display="flex" flexDirection="column" minWidth="160px">
      <Text className={classes.colLabel} level={textLevel.S} weight={fontWeight.SEMI_BOLD}>
        {label}
      </Text>
      {tel || email || link ? (
        <Link
          variant={linkType.BLANK}
          href={value ? (tel ? `tel:${value}` : email ? `mailto:${value}` : value) : '#'}
          className={clsx([classes.marginTop8, classes.link])}
          level={textLevel.M}
          weight={fontWeight.MEDIUM}
        >
          {value || 'N/A'}
        </Link>
      ) : (
        <Text className={classes.marginTop8} level={textLevel.M} weight={fontWeight.MEDIUM}>
          {value || 'N/A'}
        </Text>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        background: colors.white,
        boxShadow:
          '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" pl={3} py="12px" pr={2}>
        <Heading level={headingLevel.S} weight={fontWeight.SEMI_BOLD}>
          Patient
        </Heading>
        <Button
          className={classes.btn}
          variant={btnType.TEXT}
          onClick={() => {
            navigate(`/admin/members/${data?.userAccountId}?name=dashboard`);
          }}
          size={btnSize.SMALL}
        >
          Go to patient profile
        </Button>
      </Box>
      <Divider />

      <Box display="flex" p={4} flexWrap="wrap" gap={3} rowGap={4}>
        <Box display="flex" flexDirection="column" minWidth={160}>
          <ProfileInfo
            type="member"
            photo={data?.profileImage}
            nickName={
              data?.firstName && data?.lastName
                ? `${data?.firstName || ''} ${data?.lastName || ''}`
                : data?.fullName ?? 'No Name'
            }
            fullName={data?.uuid}
            memberId={data?.userAccountId}
          />
        </Box>
        <Col label="First Name" value={data?.firstName} />
        <Col label="Last Name" value={data?.lastName} />
        <Col label="Date of Birth" value={data?.dob ? format(new Date(data?.dob), 'MM.dd.yyyy') : ''} />
        <Col label="Gender" value={data?.gender} />
        <Col label="Phone" value={data?.phoneNumber} tel />
        {data?.demographicDetails?.email && (
          <Col label="Email" value={data?.demographicDetails.email} email />
        )}
      </Box>
      {data?.demographicDetails && (
        <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
          {data?.demographicDetails?.address1 && (
            <Col label="Address 1" value={data?.demographicDetails?.address1} />
          )}
          {data?.demographicDetails?.address2 && (
            <Col label="Address 2" value={data?.demographicDetails?.address2} />
          )}
          {data?.demographicDetails?.city && <Col label="City" value={data?.demographicDetails?.city} />}
          {data?.demographicDetails?.state && <Col label="State" value={data?.demographicDetails?.state} />}
          {data?.demographicDetails?.zip && <Col label="Zip Code" value={data?.demographicDetails?.zip} />}
        </Box>
      )}
      {(data?.externalPatientId || data?.externalPatientUrl) && (
        <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
          <SectionTitle title="External Data" />
          {data?.externalPatientId && <Col label="External Patient ID" value={data?.externalPatientId} />}
          {data?.externalPatientUrl && (
            <Col label="External Patient URL" value={data?.externalPatientId} link />
          )}
        </Box>
      )}
    </Box>
  );
};

export default Patient;
